import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import MainNavigation from './components/MainNavigation';
import AdminPanel from './components/AdminPanel';
import TeamDashboard from './TeamDashboard';
import * as Lucide from 'lucide-react';
import SelfInfo from './components/SelfInfo';

// --- KOMPONENTEN ---
import Announcements from './components/Announcements';
import AnnounceSend from './components/AnnounceSend';
import TeamMeetings from './components/TeamLeadership/TeamMeetings';
import TeamActions from './components/TeamLeadership/TeamActions';
import TeamStats from './components/TeamLeadership/TeamStats';
import TeamNotes from './components/TeamLeadership/TeamNotes';
import Absences from './components/TeamLeadership/Absences';
import Statistics from './components/ComingSoon/Statistics';
import Maps from './components/ComingSoon/Maps';
import ServerSettings from './components/ComingSoon/ServerSettings';
import SupportCases from './components/SupportCases';
import WhitelistManagement from './components/WhitelistManagement';
import Analyse from './components/Analyse';
import MissingClips from './components/MissingClips';
import FactionOverview from './components/FactionOverview';
import RefundKeys from './components/RefundKeys';
import SupportGuide from './components/SupportGuide';
import TicketGuide from './components/TicketGuide';
import TeamRules from './components/TeamRules';
import Penalties from './components/Penalties';
import StreamingManagement from './components/admin/StreamingManagement';
import Einreise from './components/Einreise';
import Giveaway from './components/Giveaway';
import Umfragen from './components/Umfragen';
import Automod from './components/Automod';
import UserCheck from './components/UserCheck';

import './Dashboard.css';

const API_BASE = process.env.REACT_APP_API_BASE;
const ADMIN_ROLE_ID = process.env.REACT_APP_ADMIN_ROLE_ID;
const TEAM_ROLE_ID = process.env.REACT_APP_TEAM_ROLE_ID;

function Dashboard({ userData, handleLogout }) {
    const [activeView, setActiveView] = useState('dashboard');
    const [guildMembers, setGuildMembers] = useState([]);
    const [membersLoading, setMembersLoading] = useState(true);
    const [serverName, setServerName] = useState('salerno City Roleplay Network');
    const [userPermissions, setUserPermissions] = useState([]);
    const [userDataState, setUserDataState] = useState(userData);
    const [memberDetails, setMemberDetails] = useState(null);
    const [isGuildMember, setIsGuildMember] = useState(false);
    const [checkingMembership, setCheckingMembership] = useState(true);
    const [dashboardStats, setDashboardStats] = useState({
        totalMembers: 0,
        onlineMembers: 0,
        teamCount: 0,
        onlineTeam: 0,
        openTickets: 0,
        upcomingEvents: 0
    });

    // State für offene Support-Cases
    const [openSupportCases, setOpenSupportCases] = useState([]);

    // State für nächste Teambesprechung
    const [nextMeetingDate, setNextMeetingDate] = useState(null);

    // State für Ticket-Kategorien
    const [ticketCategories, setTicketCategories] = useState([]);

    // State für aktive Abwesenheiten

    // State für Fraktionsübersicht

    // --- FUTURISTIC OS STATUS STATES ---
    const [systemTime, setSystemTime] = useState(new Date());
    const [envControls, setEnvControls] = useState(() => {
        try {
            const saved = localStorage.getItem('envControls');
            return saved ? JSON.parse(saved) : {
                powerManagement: false,
                securityProtocol: true,
                powerSavingMode: false,
                autoShutdown: true
            };
        } catch {
            return {
                powerManagement: false,
                securityProtocol: true,
                powerSavingMode: false,
                autoShutdown: true
            };
        }
    });
    const [chartTab, setChartTab] = useState('Mitglieder');

    // Quick Stat Metrics (nicht mehr als Simulations-States, werden durch echte Daten ersetzt)
    // Communications Log Chat messages state
    const [messages, setMessages] = useState([]);
    const [chatInput, setChatInput] = useState('');

    const fetchMessages = async () => {
        const token = localStorage.getItem('discord_token');
        if (!token) return;
        try {
            const res = await axios.get(`${API_BASE}/api/communications-log`, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 10000
            });
            if (res.data.success) {
                setMessages(res.data.messages);
            }
        } catch (err) {
            console.error('Fehler beim Laden der Nachrichten:', err);
        }
    };

    useEffect(() => {
        fetchMessages();
        if (!envControls.powerManagement) return;
        const interval = setInterval(fetchMessages, 30000);
        return () => clearInterval(interval);
    }, [envControls.powerManagement]);

    // Live clock
    useEffect(() => {
        const timer = setInterval(() => {
            setSystemTime(new Date());
        }, 1000);

        return () => {
            clearInterval(timer);
        };
    }, []);

    useEffect(() => {
        localStorage.setItem('envControls', JSON.stringify(envControls));
    }, [envControls]);

    // Helper functions for Chat
    const handleSendMessage = async (e) => {
        if (e) e.preventDefault();
        if (!chatInput.trim()) return;
        const token = localStorage.getItem('discord_token');
        if (!token) return;
        try {
            await axios.post(`${API_BASE}/api/communications-log`, {
                text: chatInput
            }, {
                headers: { Authorization: `Bearer ${token}` },
                timeout: 10000
            });
            setChatInput('');
            fetchMessages();
        } catch (err) {
            console.error('Fehler beim Senden:', err);
        }
    };

    const toggleEnv = (control) => {
        setEnvControls(prev => ({
            ...prev,
            [control]: !prev[control]
        }));
    };

    // --- BERECHTIGUNGS-LOGIK ---
    const hasAdminRole = userData?.roles?.some(role => String(role) === String(ADMIN_ROLE_ID));
    const isTeamMember = userData?.roles?.some(role => String(role) === String(TEAM_ROLE_ID));
    const hasAnyRoles = userData?.roles && userData.roles.length > 0;
    const isSuperAdmin = userData?.id === process.env.REACT_APP_SUPER_ADMIN_USER;
    const isAuthorized = isSuperAdmin || hasAdminRole || isTeamMember || hasAnyRoles;

    // --- GUILD MEMBERSHIP CHECK ---
    useEffect(() => {
        const checkGuildMembership = async () => {
            const token = localStorage.getItem('discord_token');
            if (!token || !userData?.id) {
                setCheckingMembership(false);
                setIsGuildMember(false);
                return;
            }

            // Super admins, admins, team members and users with roles bypass guild membership check
            if (isAuthorized) {
                setIsGuildMember(true);
                setCheckingMembership(false);
                return;
            }

            try {
                const response = await axios.get(`${API_BASE}/api/check-guild-membership/${userData.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setIsGuildMember(response.data.isMember);
                if (response.data.roles) {
                    setMemberDetails(response.data);
                }
            } catch (err) {
                console.error('Error checking guild membership:', err);
                setIsGuildMember(false);
            } finally {
                setCheckingMembership(false);
            }
        };

        checkGuildMembership();
    }, [userData, isAuthorized]);

    const hasPermission = (permissionName) => {
        if (userData?.id === process.env.REACT_APP_SUPER_ADMIN_USER) return true;
        if (Array.isArray(userPermissions) && userPermissions.includes(permissionName)) return true;
        if (userData?.permissions && Array.isArray(userData.permissions)) return userData.permissions.includes(permissionName);
        if (userData?.assignedRoles && Array.isArray(userData.assignedRoles)) {
            return userData.assignedRoles.some(role => role.permissions && role.permissions.includes(permissionName));
        }
        return false;
    };

    // --- OFFENE TICKETS UND CASES LADEN ---
    const loadOpenTickets = useCallback(async () => {
        const token = localStorage.getItem('discord_token');
        if (!token) return;

        try {
            const response = await axios.get(`${API_BASE}/api/discord/tickets-by-category`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                const total = response.data.total || 0;
                const categories = response.data.categories || [];
                setDashboardStats(prev => ({
                    ...prev,
                    openTickets: total
                }));
                setTicketCategories(categories);
            }
        } catch (err) {
            console.error('Error loading tickets by category:', err);
            setDashboardStats(prev => ({
                ...prev,
                openTickets: 0
            }));
            setTicketCategories([]);
        }
    }, []);

    // Offene Support-Cases laden
    const loadOpenSupportCases = useCallback(async () => {
        const token = localStorage.getItem('discord_token');
        if (!token) return;

        try {
            const response = await axios.get(`${API_BASE}/api/support-cases/open`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            if (response.data.success) {
                setOpenSupportCases(response.data.cases || []);
            }
        } catch (err) {
            console.error('Error loading open support cases:', err);
        }
    }, []);

    // Nächste Teambesprechung laden
    const loadNextMeeting = useCallback(async () => {
        const token = localStorage.getItem('discord_token');
        if (!token) return;

        try {
            const response = await axios.get(`${API_BASE}/api/team-meetings`, {
                headers: { Authorization: `Bearer ${token}` }
            });

            const meetings = response.data.filter(m => m.status !== 'archiviert');

            if (meetings.length > 0) {
                meetings.sort((a, b) => new Date(a.date) - new Date(b.date));
                const uniqueDates = [...new Set(meetings.map(m => m.date))];
                setNextMeetingDate(uniqueDates[0]);
            }
        } catch (err) {
            console.error('Error loading next meeting:', err);
        }
    }, []);

    // Aktive Abwesenheiten laden
    // Fraktionen laden
    // --- DATEN LADEN ---
    const loadDashboardData = useCallback(async () => {
        const token = localStorage.getItem('discord_token');
        if (!token) return;

        try {
            const permResponse = await axios.get(`${API_BASE}/api/my-permissions`, {
                headers: { Authorization: `Bearer ${token}` }
            });
            setUserPermissions(permResponse.data || []);
            setMembersLoading(false);

            axios.get(`${API_BASE}/api/guild-members`, { headers: { Authorization: `Bearer ${token}` } })
                .then(res => {
                    const members = res.data.members || [];
                    setGuildMembers(members);
                    setServerName(res.data.serverName || 'Salerno City Network');

                    // Filter team members only
                    const teamMembersFiltered = members.filter(member => {
                        const memberRoles = member.roles || [];
                        return memberRoles.includes(TEAM_ROLE_ID);
                    });

                    // Calculate online team members
                    const onlineTeamCount = teamMembersFiltered.filter(m =>
                        m.status === 'online' || m.status === 'idle' || m.status === 'dnd'
                    ).length;

                    // Calculate all stats
                    const onlineCount = members.filter(m => m.status === 'online').length;

                    setDashboardStats(prev => ({
                        ...prev,
                        totalMembers: members.length,
                        onlineMembers: onlineCount,
                        teamCount: teamMembersFiltered.length,
                        onlineTeam: onlineTeamCount
                    }));
                })
                .catch(err => console.error("Background loading failed:", err));

        } catch (error) {
            console.error('Error loading dashboard permissions:', error);
            if (error.response?.status === 401) handleLogout();
            setMembersLoading(false);
        }
    }, [handleLogout]);

    useEffect(() => {
        if (userData) {
            loadDashboardData();
            loadOpenTickets();
            loadOpenSupportCases();
            loadNextMeeting();
        }
    }, [userData, loadDashboardData, loadOpenTickets, loadOpenSupportCases, loadNextMeeting]);

    // Member Details laden
    useEffect(() => {
        const fetchMemberDetails = async () => {
            const token = localStorage.getItem('discord_token');
            if (!token || !userData?.id) return;
            try {
                const response = await axios.get(`${API_BASE}/api/member-details/${userData.id}`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setMemberDetails(response.data);
                setUserDataState(prev => ({
                    ...prev,
                    nickname: response.data.nick,
                    roles: response.data.roles ? response.data.roles.map(r => r.id) : []
                }));
            } catch (err) { console.error('Member details error:', err); }
        };
        fetchMemberDetails();
    }, [userData]);

    // Polling für Berechtigungen, Tickets und Support-Cases
    useEffect(() => {
        if (!userData) return;
        const intervalMs = envControls.powerSavingMode ? 600000 : 120000;
        const interval = setInterval(async () => {
            const token = localStorage.getItem('discord_token');
            try {
                const res = await axios.get(`${API_BASE}/api/my-permissions`, {
                    headers: { Authorization: `Bearer ${token}` }
                });
                setUserPermissions(res.data || []);

                loadOpenTickets();
                loadOpenSupportCases();
                loadNextMeeting();
            } catch (e) { /* silent fail */ }
        }, intervalMs);
        return () => clearInterval(interval);
    }, [userData, loadOpenTickets, loadOpenSupportCases, loadNextMeeting, envControls.powerSavingMode]);

    // --- PERMISSION CHECK FOR VIEWS ---
    const canAccessView = (viewId) => {
        // Super admin can access everything
        if (userData?.id === process.env.REACT_APP_SUPER_ADMIN_USER) return true;

        const viewPermissions = {
            'admin': () => hasPermission('admin_access') || hasPermission('view_admin_panel'),
            'commands': () => hasPermission('manage_commands'),
            'team-meetings': () => hasPermission('manage_meetings') || hasPermission('team_view'),
            'team-actions': () => hasPermission('manage_team_members'),
            'team-notes': () => hasPermission('manage_team_members'),
            'team-stats': () => hasPermission('view_whitelist_stats'),
            'absences': () => hasPermission('view_whitelist_stats'),
            'server-settings': () => hasPermission('admin_access'),
            'backups': () => hasPermission('admin_access'),
            'announcements': () => true,
            'support': () => true,
            'whitelist': () => envControls.securityProtocol && (hasPermission('manage_whitelist') || hasPermission('view_whitelist_stats')),
            'analysieren': () => hasPermission('view_analysis'),
            'missing-clips': () => hasPermission('view_analysis'),
            'faction-overview': () => hasPermission('view_faction_overview'),
            'rückerstattungen': () => hasPermission('manage_refunds'),
            'streaming': () => hasPermission('manage_streamers'),
            'einreise': () => true,
            'giveaway': () => hasPermission('manage_giveaways'),
            'umfragen': () => hasPermission('manage_polls'),
            'automod': () => hasPermission('manage_automod'),
            'user-check': () => hasPermission('view_user_check'),
            // Views available to all team members
            'dashboard': () => true,
            'team': () => true,
            'self-info': () => true,
            'support-guide': () => true,
            'ticket-guide': () => true,
            'penalties': () => true,
            'team-rules': () => true,
            'maps': () => true,
            'statistics': () => true
        };

        const checkFn = viewPermissions[viewId];
        if (!checkFn) return true; // Unknown views are allowed
        return checkFn();
    };

    // --- ACCESS DENIED COMPONENT ---
    const renderAccessDenied = () => (
        <div className="dashboard-container">
            <div className="widget-card">
                <div className="access-denied">
                    <div className="access-denied-icon">🚫</div>
                    <h2>Zugang verweigert</h2>
                    <p>Sie haben keine Berechtigung, diese Seite anzuzeigen.</p>
                    <p className="muted">Kontaktieren Sie einen Administrator, wenn Sie glauben, dass dies ein Fehler ist.</p>
                    <button className="btn btn-primary" onClick={() => navigateTo('dashboard')}>
                        Zum Dashboard zurückkehren
                    </button>
                </div>
            </div>
        </div>
    );

    // --- NAVIGATION FUNCTIONS ---
    const navigateTo = (view) => {
        // Check if user has permission to access this view
        if (!canAccessView(view)) {
            // Optionally show a warning toast or alert
            console.warn('Access denied to view:', view);
            return; // Block navigation
        }
        setActiveView(view);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    // Format next meeting date for display
    const formatNextMeetingDate = (dateString) => {
        if (!dateString) return 'Keine geplant';
        const date = new Date(dateString);
        return date.toLocaleDateString('de-DE', {
            day: '2-digit',
            month: '2-digit',
            year: 'numeric'
        });
    };

    // Format next meeting time for display
    const formatNextMeetingTime = (dateString) => {
        if (!dateString) return '';
        if (dateString.includes('T') && dateString.split('T')[1].length > 5) {
            const time = dateString.split('T')[1].slice(0, 5);
            return time;
        }
        return '';
    };

    if (!userData) return null;

    // --- GUEST ACCESS DENIED ---
    if (!checkingMembership && !isGuildMember) {
        return (
            <div className="dashboard-container">
                <MainNavigation
                    currentView={activeView}
                    setCurrentView={navigateTo}
                    isAdmin={hasAdminRole}
                    userData={userData}
                    handleLogout={handleLogout}
                    hasPermission={hasPermission}
                />
                <div className="widget-card" style={{ margin: '2rem', textAlign: 'center' }}>
                    <div className="access-denied">
                        <div className="access-denied-icon">🚫</div>
                        <h2>Kein Zugriff</h2>
                        <p>Du bist kein Mitglied des Discord-Servers.</p>
                        <p className="muted">Bitte trete dem Server bei, um das Dashboard nutzen zu können.</p>
                        <button className="btn btn-primary" onClick={handleLogout}>
                            Abmelden
                        </button>
                    </div>
                </div>
            </div>
        );
    }

    if (checkingMembership) {
        return (
            <div className="dashboard-container">
                <div className="widget-card" style={{ margin: '2rem', textAlign: 'center' }}>
                    <div className="loading-container">
                        <div className="spinner"></div>
                        <h2>Überprüfe Zugriffsrechte...</h2>
                    </div>
                </div>
            </div>
        );
    }

    // --- RENDER DASHBOARD HOME ---
    const renderDashboardHome = () => {
        return (
            <div className="dashboard-grid-layout">
                {/* Left/Middle Main Column */}
                <div className="dashboard-col-main">
                    
                    {/* Header */}
                    <div className="dashboard-header">
                        <div className="dashboard-title-group">
                            <Lucide.LayoutDashboard className="dashboard-title-icon" size={22} />
                            <h2>Panel Übersicht</h2>
                        </div>
                        <div className="dashboard-actions-group">
                            {envControls.powerManagement && (
                                <span className="live-badge">
                                    <span className="live-pulse-dot" />
                                    LIVE
                                </span>
                            )}
                            <button className="refresh-btn" onClick={() => loadDashboardData()} title="Daten aktualisieren">
                                <Lucide.RefreshCw size={14} />
                            </button>
                        </div>
                    </div>

                    {/* Quick Stats Row */}
                    <div className="dashboard-stats-grid">
                        {/* Server Mitglieder Card */}
                        <div className="dashboard-stat-card cyan-theme">
                            <div className="stat-card-header">
                                <span className="stat-card-title">Server Mitglieder</span>
                                <Lucide.Users size={18} className="card-icon-cyan" />
                            </div>
                            <div className="stat-card-body">
                                <div className="stat-card-value">{dashboardStats.totalMembers}</div>
                                <div className="stat-card-sub">{dashboardStats.onlineMembers} online</div>
                                <div className="stat-card-mini-chart">
                                    <div className="mini-chart-bar bg-cyan" style={{ width: dashboardStats.totalMembers > 0 ? `${Math.min(100, (dashboardStats.onlineMembers / dashboardStats.totalMembers) * 100)}%` : '0%' }} />
                                </div>
                            </div>
                        </div>

                        {/* Team Mitglieder Card */}
                        <div className="dashboard-stat-card purple-theme">
                            <div className="stat-card-header">
                                <span className="stat-card-title">Team</span>
                                <Lucide.UserCheck size={18} className="card-icon-purple" />
                            </div>
                            <div className="stat-card-body">
                                <div className="stat-card-value">{dashboardStats.teamCount}</div>
                                <div className="stat-card-sub">{dashboardStats.onlineTeam} aktiv</div>
                                <div className="stat-card-mini-chart">
                                    <div className="mini-chart-bar bg-purple" style={{ width: dashboardStats.teamCount > 0 ? `${Math.min(100, (dashboardStats.onlineTeam / dashboardStats.teamCount) * 100)}%` : '0%' }} />
                                </div>
                            </div>
                        </div>

                        {/* Offene Tickets Card */}
                        <div className="dashboard-stat-card blue-theme">
                            <div className="stat-card-header">
                                <span className="stat-card-title">Offene Tickets</span>
                                <Lucide.Ticket size={18} className="card-icon-blue" />
                            </div>
                            <div className="stat-card-body">
                                <div className="stat-card-value">{dashboardStats.openTickets}</div>
                                <div className="stat-card-sub">{openSupportCases.length} Support Cases</div>
                                <div className="stat-card-mini-chart">
                                    <div className="mini-chart-bar bg-blue" style={{ width: `${Math.min(100, dashboardStats.openTickets * 5)}%` }} />
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Panel Aktivitäts-Übersicht */}
                    <div className="system-chart-panel">
                        <div className="chart-panel-header">
                            <div className="chart-tabs">
                                {['Mitglieder', 'Tickets', 'Team'].map(tab => (
                                    <button 
                                        key={tab} 
                                        className={`chart-tab-btn ${chartTab === tab ? 'active' : ''}`}
                                        onClick={() => setChartTab(tab)}
                                    >
                                        {tab}
                                    </button>
                                ))}
                            </div>
                            <div className="chart-legend">
                                <span className="legend-dot-item"><span className="legend-dot bg-cyan" /> Online</span>
                                <span className="legend-dot-item"><span className="legend-dot bg-purple" /> Gesamt</span>
                                <span className="legend-dot-item"><span className="legend-dot bg-blue" /> Team</span>
                            </div>
                        </div>

                        <div className="chart-body-wrapper">
                            {chartTab === 'Mitglieder' && (
                                <div className="panel-stat-overview">
                                    <div className="panel-stat-big-row">
                                        <div className="panel-stat-big-item">
                                            <Lucide.Users size={32} className="card-icon-cyan" />
                                            <div className="panel-stat-big-content">
                                                <span className="panel-stat-big-value cyan">{dashboardStats.totalMembers}</span>
                                                <span className="panel-stat-big-label">Gesamt Mitglieder</span>
                                            </div>
                                        </div>
                                        <div className="panel-stat-big-item">
                                            <Lucide.CircleDot size={32} className="card-icon-green" />
                                            <div className="panel-stat-big-content">
                                                <span className="panel-stat-big-value green">{dashboardStats.onlineMembers}</span>
                                                <span className="panel-stat-big-label">Aktuell Online</span>
                                            </div>
                                        </div>
                                        <div className="panel-stat-big-item">
                                            <Lucide.TrendingUp size={32} className="card-icon-purple" />
                                            <div className="panel-stat-big-content">
                                                <span className="panel-stat-big-value purple">
                                                    {dashboardStats.totalMembers > 0 ? Math.round((dashboardStats.onlineMembers / dashboardStats.totalMembers) * 100) : 0}%
                                                </span>
                                                <span className="panel-stat-big-label">Online-Rate</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="panel-progress-section">
                                        <div className="panel-progress-label">
                                            <span>Online-Anteil</span>
                                            <span className="cyan">{dashboardStats.onlineMembers} / {dashboardStats.totalMembers}</span>
                                        </div>
                                        <div className="level-progress-bar">
                                            <div className="level-progress-fill" style={{ width: dashboardStats.totalMembers > 0 ? `${Math.min(100, (dashboardStats.onlineMembers / dashboardStats.totalMembers) * 100)}%` : '0%' }} />
                                        </div>
                                    </div>
                                </div>
                            )}
                            {chartTab === 'Tickets' && (
                                <div className="panel-stat-overview">
                                    <div className="panel-stat-big-row">
                                        <div className="panel-stat-big-item">
                                            <Lucide.Ticket size={32} className="card-icon-blue" />
                                            <div className="panel-stat-big-content">
                                                <span className="panel-stat-big-value blue">{dashboardStats.openTickets}</span>
                                                <span className="panel-stat-big-label">Kanäle Gesamt</span>
                                            </div>
                                        </div>
                                        <div className="panel-stat-big-item">
                                            <Lucide.MessageCircle size={32} className="card-icon-orange" />
                                            <div className="panel-stat-big-content">
                                                <span className="panel-stat-big-value orange">{openSupportCases.length}</span>
                                                <span className="panel-stat-big-label">Support Cases</span>
                                            </div>
                                        </div>
                                        <div className="panel-stat-big-item">
                                            <Lucide.CheckCircle2 size={32} className="card-icon-green" />
                                            <div className="panel-stat-big-content">
                                                <span className="panel-stat-big-value green">{ticketCategories.length > 0 ? 'Aktiv' : 'Keine'}</span>
                                                <span className="panel-stat-big-label">Kategorien</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="panel-progress-section">
                                        <div className="panel-progress-label">
                                            <span>Kanäle nach Kategorie</span>
                                            <span className="blue">{dashboardStats.openTickets} Kanäle insgesamt</span>
                                        </div>
                                        <div className="ticket-categories-list">
                                            {ticketCategories.map(cat => (
                                                <div key={cat.category} className="ticket-category-item">
                                                    <span className="ticket-category-name">{cat.category}</span>
                                                    <span className="ticket-category-count">{cat.count}</span>
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                </div>
                            )}
                            {chartTab === 'Team' && (
                                <div className="panel-stat-overview">
                                    <div className="panel-stat-big-row">
                                        <div className="panel-stat-big-item">
                                            <Lucide.UserCheck size={32} className="card-icon-purple" />
                                            <div className="panel-stat-big-content">
                                                <span className="panel-stat-big-value purple">{dashboardStats.teamCount}</span>
                                                <span className="panel-stat-big-label">Team Mitglieder</span>
                                            </div>
                                        </div>
                                        <div className="panel-stat-big-item">
                                            <Lucide.CircleDot size={32} className="card-icon-green" />
                                            <div className="panel-stat-big-content">
                                                <span className="panel-stat-big-value green">{dashboardStats.onlineTeam}</span>
                                                <span className="panel-stat-big-label">Team Online</span>
                                            </div>
                                        </div>
                                        <div className="panel-stat-big-item">
                                            <Lucide.CalendarDays size={32} className="card-icon-cyan" />
                                            <div className="panel-stat-big-content">
                                                <span className="panel-stat-big-value cyan" style={{ fontSize: '1rem' }}>{nextMeetingDate ? formatNextMeetingDate(nextMeetingDate) : 'Keine'}</span>
                                                <span className="panel-stat-big-label">Nächste Teambesprechung</span>
                                            </div>
                                        </div>
                                    </div>
                                    <div className="panel-progress-section">
                                        <div className="panel-progress-label">
                                            <span>Team Online-Rate</span>
                                            <span className="purple">{dashboardStats.onlineTeam} / {dashboardStats.teamCount} aktiv</span>
                                        </div>
                                        <div className="level-progress-bar">
                                            <div className="level-progress-fill bg-purple" style={{ width: dashboardStats.teamCount > 0 ? `${Math.min(100, (dashboardStats.onlineTeam / dashboardStats.teamCount) * 100)}%` : '0%' }} />
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Bottom Cards Block */}
                    <div className="bottom-widgets-grid">
                        
                        {/* Panel Status Card */}
                        <div className="glass-panel-card security-status-card">
                            <div className="panel-card-header green-theme">
                                <Lucide.BarChart3 size={18} />
                                <h3>Panel Status</h3>
                            </div>
                            <div className="panel-card-body">
                                <div className="security-checks-list">
                                    <div className="check-item">
                                        <span>Discord Bot</span>
                                        <span className="check-badge active">Online</span>
                                    </div>
                                    <div className="check-item">
                                        <span>Whitelist System</span>
                                        <span className={`check-badge ${envControls.securityProtocol ? 'active' : ''}`}>{envControls.securityProtocol ? 'Aktiv' : 'Deaktiviert'}</span>
                                    </div>
                                    <div className="check-item">
                                        <span>Support System</span>
                                        <span className={`check-badge ${envControls.autoShutdown ? 'active' : 'warning'}`}>{envControls.autoShutdown ? 'Aktiv' : 'Pausiert'}</span>
                                    </div>
                                </div>
                                <div className="threat-database-info">
                                    Server: <span className="cyan-text">{serverName}</span>
                                </div>
                                <div className="security-level-slider">
                                    <div className="level-labels">
                                        <span>Team Auslastung</span>
                                        <span>{dashboardStats.teamCount > 0 ? `${Math.round((dashboardStats.onlineTeam / dashboardStats.teamCount) * 100)}%` : '0%'}</span>
                                    </div>
                                    <div className="level-progress-bar">
                                        <div className="level-progress-fill" style={{ width: dashboardStats.teamCount > 0 ? `${Math.min(100, (dashboardStats.onlineTeam / dashboardStats.teamCount) * 100)}%` : '0%' }} />
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Benachrichtigungen Card */}
                        <div className="glass-panel-card system-alerts-card">
                            <div className="panel-card-header orange-theme">
                                <Lucide.Bell size={18} />
                                <h3>Benachrichtigungen</h3>
                            </div>
                            <div className="panel-card-body">
                                <div className="alerts-history-list">
                                    {envControls.autoShutdown && nextMeetingDate && (
                                        <div className="alert-history-item info">
                                            <div className="alert-meta">
                                                <Lucide.CalendarDays size={12} className="alert-icon-state text-cyan" />
                                                <span className="alert-title">Teambesprechung</span>
                                                <span className="alert-time">Geplant</span>
                                            </div>
                                            <p className="alert-desc">Sitzung am {formatNextMeetingDate(nextMeetingDate)} {formatNextMeetingTime(nextMeetingDate) ? `um ${formatNextMeetingTime(nextMeetingDate)} Uhr` : ''}</p>
                                        </div>
                                    )}
                                    {envControls.autoShutdown && openSupportCases.length > 0 && (
                                        <div className="alert-history-item warning">
                                            <div className="alert-meta">
                                                <Lucide.AlertTriangle size={12} className="alert-icon-state text-orange" />
                                                <span className="alert-title">Offene Support Cases</span>
                                                <span className="alert-time">Support</span>
                                            </div>
                                            <p className="alert-desc">{openSupportCases.length} {openSupportCases.length === 1 ? 'Fall' : 'Fälle'} ausstehend – Bearbeitung erforderlich</p>
                                        </div>
                                    )}
                                    {envControls.autoShutdown && dashboardStats.openTickets > 0 && (
                                        <div className="alert-history-item warning">
                                            <div className="alert-meta">
                                                <Lucide.Ticket size={12} className="alert-icon-state text-blue" />
                                                <span className="alert-title">Offene Tickets</span>
                                                <span className="alert-time">Discord</span>
                                            </div>
                                            <p className="alert-desc">{dashboardStats.openTickets} {dashboardStats.openTickets === 1 ? 'Ticket wartet' : 'Tickets warten'} auf Bearbeitung</p>
                                        </div>
                                    )}
                                    {dashboardStats.onlineTeam > 0 && (
                                        <div className="alert-history-item success">
                                            <div className="alert-meta">
                                                <Lucide.CheckCircle2 size={12} className="alert-icon-state text-green" />
                                                <span className="alert-title">Team Online</span>
                                                <span className="alert-time">Aktiv</span>
                                            </div>
                                            <p className="alert-desc">{dashboardStats.onlineTeam} Team-{dashboardStats.onlineTeam === 1 ? 'Mitglied ist' : 'Mitglieder sind'} derzeit online</p>
                                        </div>
                                    )}
                                    {envControls.autoShutdown && !nextMeetingDate && openSupportCases.length === 0 && dashboardStats.openTickets === 0 && (
                                        <div className="alert-history-item info">
                                            <div className="alert-meta">
                                                <Lucide.CheckCircle2 size={12} className="alert-icon-state text-green" />
                                                <span className="alert-title">Alles ruhig</span>
                                                <span className="alert-time">Jetzt</span>
                                            </div>
                                            <p className="alert-desc">Keine ausstehenden Benachrichtigungen</p>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>

                        {/* Communications Log Card */}
                        <div className="glass-panel-card communications-log-card">
                            <div className="panel-card-header blue-theme">
                                <Lucide.MessageSquare size={18} />
                                <h3>Communications Log</h3>
                                <span className="log-badge">{messages.length} Messages</span>
                            </div>
                            <div className="panel-card-body chat-flex-layout">
                                <div className="chat-messages-container">
                                    {messages.map((msg, idx) => (
                                        <div className="chat-msg-row" key={msg.id || idx}>
                                            <img src={msg.avatar} alt="Avatar" className="chat-msg-avatar" onError={(e) => { e.target.src = 'https://cdn.discordapp.com/embed/avatars/0.png' }} />
                                            <div className="chat-msg-content-wrapper">
                                                <div className="chat-msg-header">
                                                    <span className="chat-msg-sender">{msg.sender}</span>
                                                    <span className="chat-msg-time">{msg.time}</span>
                                                </div>
                                                <p className="chat-msg-text">{msg.text}</p>
                                            </div>
                                        </div>
                                    ))}
                                </div>
                                <form onSubmit={handleSendMessage} className="chat-input-row">
                                    <input 
                                        type="text" 
                                        placeholder="Type a message..." 
                                        value={chatInput}
                                        onChange={(e) => setChatInput(e.target.value)}
                                    />
                                    <div className="chat-input-actions">
                                        <button type="button" className="chat-action-btn" title="Microphone">
                                            <Lucide.Mic size={14} />
                                        </button>
                                        <button type="submit" className="chat-action-btn send" title="Send message">
                                            <Lucide.Send size={14} />
                                        </button>
                                    </div>
                                </form>
                            </div>
                        </div>

                    </div>
                </div>

                {/* Right Column Layout */}
                <div className="dashboard-col-sidebar">
                    
                    {/* Zeit Card */}
                    <div className="glass-panel-card system-time-card">
                        <div className="time-card-header">
                            <span className="time-card-title-label">PANEL ZEIT</span>
                            <Lucide.Clock size={16} className="text-muted" />
                        </div>
                        <div className="time-card-body">
                            <div className="live-time-display">
                                {systemTime.toLocaleTimeString('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit' })}
                            </div>
                            <div className="live-date-display">
                                {systemTime.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })}
                            </div>
                            <div className="uptime-timezone-info">
                                 <div className="info-row">
                                     <span className="label">Server</span>
                                     <span className="value" style={{ fontSize: '0.7rem', maxWidth: '120px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{serverName}</span>
                                 </div>
                                 {envControls.autoShutdown && (
                                     <div className="info-row">
                                         <span className="label">Nächste Teambesprechung</span>
                                         <span className="value" style={{ fontSize: '0.7rem' }}>{nextMeetingDate ? formatNextMeetingDate(nextMeetingDate) : 'Keine'}</span>
                                     </div>
                                 )}
                            </div>
                        </div>
                    </div>

                    {/* Quick Actions Card */}
                    <div className="glass-panel-card quick-actions-card">
                        <div className="panel-card-header-simple">
                            <h3>Quick Actions</h3>
                        </div>
                        <div className="quick-actions-grid">
                            <button className="quick-action-btn-item" onClick={() => navigateTo('einreise')}>
                                <Lucide.Plane size={18} className="btn-icon" />
                                <span>Einreise</span>
                            </button>
                            <button className="quick-action-btn-item" onClick={() => navigateTo('support')}>
                                <Lucide.HeadphonesIcon size={18} className="btn-icon" />
                                <span>Support</span>
                            </button>
                            <button className="quick-action-btn-item" onClick={() => navigateTo('team-meetings')}>
                                <Lucide.CalendarDays size={18} className="btn-icon" />
                                <span>Meetings</span>
                            </button>
                            <button className="quick-action-btn-item" onClick={() => navigateTo('announcements')}>
                                <Lucide.Megaphone size={18} className="btn-icon" />
                                <span>Ankündigungen</span>
                            </button>
                        </div>
                    </div>

                    {/* Team Status Card */}
                    <div className="glass-panel-card resource-allocation-card">
                        <div className="panel-card-header-simple">
                            <h3>Team Status</h3>
                        </div>
                        <div className="allocation-body">
                            <div className="allocation-item">
                                <div className="allocation-header">
                                    <span>Team Online</span>
                                    <span className="cyan">{dashboardStats.onlineTeam} / {dashboardStats.teamCount}</span>
                                </div>
                                <div className="allocation-progressbar">
                                    <div className="allocation-progress-fill bg-cyan" style={{ width: dashboardStats.teamCount > 0 ? `${Math.min(100, (dashboardStats.onlineTeam / dashboardStats.teamCount) * 100)}%` : '0%' }} />
                                </div>
                            </div>
                            
                            <div className="allocation-item">
                                <div className="allocation-header">
                                    <span>Offene Tickets</span>
                                    <span className="blue">{dashboardStats.openTickets} Tickets</span>
                                </div>
                                <div className="allocation-progressbar">
                                    <div className="allocation-progress-fill bg-blue" style={{ width: `${Math.min(100, dashboardStats.openTickets * 5)}%` }} />
                                </div>
                            </div>

                            <div className="allocation-item">
                                <div className="allocation-header">
                                    <span>Support Cases</span>
                                    <span className="purple">{openSupportCases.length} Fälle</span>
                                </div>
                                <div className="allocation-progressbar">
                                    <div className="allocation-progress-fill bg-purple" style={{ width: `${Math.min(100, openSupportCases.length * 10)}%` }} />
                                </div>
                            </div>

                            {envControls.autoShutdown && (
                                <div className="allocation-item">
                                    <div className="allocation-header">
                                        <span>Nächste Teambesprechung</span>
                                        <span className="slider-val" style={{ color: 'var(--color-cyan)', fontSize: '0.75rem' }}>
                                            {nextMeetingDate ? formatNextMeetingDate(nextMeetingDate) : 'Keine geplant'}
                                        </span>
                                    </div>
                                    <div className="allocation-progressbar">
                                        <div className="allocation-progress-fill bg-green" style={{ width: nextMeetingDate ? '100%' : '0%' }} />
                                    </div>
                                </div>
                            )}
                        </div>
                    </div>

                    {/* Panel Einstellungen Card */}
                    <div className="glass-panel-card environment-controls-card">
                        <div className="panel-card-header-simple">
                            <h3>Panel Einstellungen</h3>
                        </div>
                        <div className="controls-body">
                            <div className="control-switch-row">
                                <div className="control-label-wrapper">
                                    <Lucide.Bell size={16} className="control-icon text-cyan" />
                                    <span>Benachrichtigungen</span>
                                </div>
                                <button 
                                    className={`control-toggle-switch ${envControls.powerManagement ? 'active' : ''}`}
                                    onClick={() => toggleEnv('powerManagement')}
                                >
                                    <span className="toggle-slider" />
                                </button>
                            </div>

                            <div className="control-switch-row">
                                <div className="control-label-wrapper">
                                    <Lucide.ShieldCheck size={16} className="control-icon text-green" />
                                    <span>Whitelist-System</span>
                                </div>
                                <button 
                                    className={`control-toggle-switch ${envControls.securityProtocol ? 'active' : ''}`}
                                    onClick={() => toggleEnv('securityProtocol')}
                                >
                                    <span className="toggle-slider" />
                                </button>
                            </div>

                            <div className="control-switch-row">
                                <div className="control-label-wrapper">
                                    <Lucide.Bot size={16} className="control-icon text-purple" />
                                    <span>Bot Antworten</span>
                                </div>
                                <button 
                                    className={`control-toggle-switch ${envControls.powerSavingMode ? 'active' : ''}`}
                                    onClick={() => toggleEnv('powerSavingMode')}
                                >
                                    <span className="toggle-slider" />
                                </button>
                            </div>

                            <div className="control-switch-row">
                                <div className="control-label-wrapper">
                                    <Lucide.CalendarCheck size={16} className="control-icon text-orange" />
                                    <span>Meeting-Erinnerungen</span>
                                </div>
                                <button 
                                    className={`control-toggle-switch ${envControls.autoShutdown ? 'active' : ''}`}
                                    onClick={() => toggleEnv('autoShutdown')}
                                >
                                    <span className="toggle-slider" />
                                </button>
                            </div>
                        </div>
                    </div>

                </div>
            </div>
        );
    };

    // --- CONTENT RENDER ---
    const renderContent = () => {
        // Check if user has permission to view this page
        if (!canAccessView(activeView)) {
            return renderAccessDenied();
        }

        if (membersLoading && activeView === 'admin') {
            return (
                <div className="dashboard-container">
                    <div className="widget-loading">
                        <div className="spinner"></div>
                    </div>
                </div>
            );
        }

        switch (activeView) {
            case 'team-meetings': return <TeamMeetings />;
            case 'team-actions': return <TeamActions />;
            case 'team-notes': return <TeamNotes />;
            case 'team-stats': return <TeamStats userData={userData} hasPermission={hasPermission} />;
            case 'absences': return <Absences />;
            case 'admin': return <AdminPanel userData={userData} refreshPermissions={loadDashboardData} initialTab="dashboard" />;
            case 'commands': return <AdminPanel userData={userData} refreshPermissions={loadDashboardData} initialTab="commands" />;
            case 'server-settings': return <ServerSettings />;
            case 'backups': return <AnnounceSend />;
            case 'team': return <TeamDashboard guildMembers={guildMembers} membersLoading={membersLoading} serverName={serverName} />;
            case 'announcements': return <Announcements userData={userDataState} hasPermission={hasPermission} />;
            case 'support': return <SupportCases userData={userData} hasPermission={hasPermission} />;
            case 'support-guide': return <SupportGuide />;
            case 'ticket-guide': return <TicketGuide />;
            case 'penalties': return <Penalties />;
            case 'team-rules': return <TeamRules />;
            case 'whitelist': return <WhitelistManagement userData={userDataState} hasPermission={hasPermission} />;
            case 'self-info': return <SelfInfo userData={userData} memberDetails={memberDetails} />;
            case 'analysieren': return <Analyse />;
            case 'missing-clips': return <MissingClips />;
            case 'faction-overview': return <FactionOverview userData={userData} hasPermission={hasPermission} />;
            case 'rückerstattungen': return <RefundKeys userData={userData} hasPermission={hasPermission} />;
            case 'streaming': return <StreamingManagement hasPermission={hasPermission} />;
            case 'maps': return <Maps />;
            case 'statistics': return <Statistics />;
            case 'einreise': return <Einreise userData={userData} />;
            case 'giveaway': return <Giveaway />;
            case 'umfragen': return <Umfragen />;
            case 'automod': return <Automod />;
            case 'user-check': return <UserCheck />;
            case 'dashboard':
            default:
                return renderDashboardHome();
        }
    };

    return (
        <div className="app-shell">
            <MainNavigation
                currentView={activeView}
                setCurrentView={navigateTo}
                isAdmin={() => hasAdminRole || hasPermission('admin_access')}
                userData={userDataState}
                handleLogout={handleLogout}
                hasPermission={hasPermission}
            />
            <div className="main-content">
                {renderContent()}
            </div>
        </div>
    );
}

export default Dashboard;
